/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { jsx, cssMap } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { useSyncedThinkingStatus } from '../../hooks/useSyncedThinkingStatus';

import { Thinking } from './Thinking';
import { ThinkingComplete } from './ThinkingComplete';
import { ThinkingTransitionWrapper } from './ThinkingTransitionWrapper';

type ThinkingContentComponentProps = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
};

const styles = cssMap({
	container: {
		position: 'fixed',
		bottom: token('space.300'),
		minWidth: 400,
		zIndex: 510, // same as akEditorFloatingDialogZIndex
		left: '50%',
		transform: 'translateX(-50%)',
	},
});

export function ThinkingContentComponent({
	api,
}: ThinkingContentComponentProps): JSX.Element | null {
	const thinkingStatus = useSyncedThinkingStatus(api);

	const { firstSuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => ({
			firstSuggestionId: state?.aiSuggestionsState?.orderedSuggestionIds?.[0],
		}),
	);

	const handleStop = useCallback(() => {
		api?.aiSuggestions?.actions.stopThinking();
	}, [api]);

	const handleClose = useCallback(() => {
		api?.aiSuggestions?.actions.stopThinking();
	}, [api]);

	const onViewSuggestions = useCallback(() => {
		api?.aiSuggestions?.actions.stopThinking();
		api?.aiSuggestions?.actions.selectSuggestion(firstSuggestionId ?? null);
	}, [api, firstSuggestionId]);

	const handleReturnToIdle = useCallback(() => {
		if (!firstSuggestionId) {
			api?.aiSuggestions?.actions.stopThinking();
		}
	}, [api, firstSuggestionId]);

	return (
		<div css={styles.container}>
			<ThinkingTransitionWrapper
				thinkingStatus={thinkingStatus}
				isHidden={false} // will be wired up to the Rovo mounted state
				onComplete={handleReturnToIdle}
			>
				{(phase) =>
					phase === 'thinking' ? (
						<Thinking onStop={handleStop} />
					) : (
						<ThinkingComplete
							onClose={handleClose}
							hasSuggestions={!!firstSuggestionId}
							onViewSuggestions={onViewSuggestions}
						/>
					)
				}
			</ThinkingTransitionWrapper>
		</div>
	);
}
